import { ref } from 'vue';

const RUNTIME_PATH = '/mediapipe/selfie_segmentation';
const BACKGROUND_PATHS = {
  office: '/meet-backgrounds/office.png',
  'bright-loft': '/meet-backgrounds/bright-loft.png',
  library: '/meet-backgrounds/executive-library.png',
  studio: '/meet-backgrounds/creative-studio.png',
};
let segmentationRuntimePromise = null;

function loadSegmentationRuntime() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Person segmentation requires a browser.'));
  if (window.SelfieSegmentation) return Promise.resolve(window.SelfieSegmentation);
  if (segmentationRuntimePromise) return segmentationRuntimePromise;
  segmentationRuntimePromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${RUNTIME_PATH}/selfie_segmentation.js`;
    script.async = true;
    script.dataset.platformaMeetSegmentation = 'true';
    script.onload = () => {
      if (window.SelfieSegmentation) resolve(window.SelfieSegmentation);
      else reject(new Error('The person-segmentation runtime did not initialize.'));
    };
    script.onerror = () => reject(new Error('The person-segmentation runtime could not be loaded.'));
    document.head.appendChild(script);
  }).catch((runtimeError) => {
    segmentationRuntimePromise = null;
    throw runtimeError;
  });
  return segmentationRuntimePromise;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

export function useVirtualBackground() {
  const effect = ref('none');
  const loading = ref(false);
  const error = ref('');
  const processedStream = ref(null);
  let sourceStream = null;
  let sourceVideo = null;
  let canvas = null;
  let context = null;
  let foregroundCanvas = null;
  let foregroundContext = null;
  let maskCanvas = null;
  let maskContext = null;
  let maskAnalysisCanvas = null;
  let maskAnalysisContext = null;
  let maskConfidenceHistory = null;
  let segmenter = null;
  const backgroundImages = new Map();
  let active = false;
  let frameHandle = null;

  const ensureBackgroundImage = async (background) => {
    const path = BACKGROUND_PATHS[background];
    if (!path) return null;
    const existing = backgroundImages.get(background);
    if (existing) return await existing;
    const pending = loadImage(path);
    backgroundImages.set(background, pending);
    try {
      const image = await pending;
      backgroundImages.set(background, image);
      return image;
    } catch (imageError) {
      backgroundImages.delete(background);
      throw imageError;
    }
  };

  const scheduleFrame = () => {
    if (!active || !sourceVideo) return;
    if (typeof sourceVideo.requestVideoFrameCallback === 'function') frameHandle = sourceVideo.requestVideoFrameCallback(processFrame);
    else frameHandle = window.setTimeout(processFrame, 34);
  };

  const processFrame = async () => {
    if (!active || !segmenter || !sourceVideo) return;
    try {
      if (sourceVideo.readyState >= 2) await segmenter.send({ image: sourceVideo });
    } catch (processingError) {
      error.value = processingError?.message || 'The background effect could not process this frame.';
    } finally {
      scheduleFrame();
    }
  };

  const updateSmoothedMask = (segmentationMask) => {
    if (!maskContext || !maskAnalysisContext || !maskCanvas || !maskAnalysisCanvas || !canvas) return null;
    const { width, height } = canvas;
    const analysisWidth = maskAnalysisCanvas.width;
    const analysisHeight = maskAnalysisCanvas.height;
    maskAnalysisContext.save();
    maskAnalysisContext.clearRect(0, 0, analysisWidth, analysisHeight);
    maskAnalysisContext.filter = 'blur(.55px)';
    maskAnalysisContext.drawImage(segmentationMask, 0, 0, analysisWidth, analysisHeight);
    maskAnalysisContext.restore();

    const frame = maskAnalysisContext.getImageData(0, 0, analysisWidth, analysisHeight);
    const pixels = frame.data;
    const pixelCount = analysisWidth * analysisHeight;
    if (!maskConfidenceHistory || maskConfidenceHistory.length !== pixelCount) maskConfidenceHistory = new Float32Array(pixelCount);

    // MediaPipe builds expose confidence either in alpha or in the grayscale
    // channels. Detect the active channel so this stays correct across both.
    let alphaMinimum = 255;
    let alphaMaximum = 0;
    for (let index = 3; index < pixels.length; index += 64) {
      alphaMinimum = Math.min(alphaMinimum, pixels[index]);
      alphaMaximum = Math.max(alphaMaximum, pixels[index]);
    }
    const confidenceInAlpha = alphaMaximum - alphaMinimum > 16;

    for (let pixel = 0, index = 0; pixel < pixelCount; pixel += 1, index += 4) {
      const confidence = confidenceInAlpha
        ? pixels[index + 3] / 255
        : (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 765;
      const previous = maskConfidenceHistory[pixel];
      // Acquire the current outline quickly. Retain only a small amount of the
      // previous frame when confidence drops, preventing flashes without a
      // trailing silhouette during movement.
      const response = confidence >= previous ? 0.84 : 0.74;
      const smoothed = previous + ((confidence - previous) * response);
      maskConfidenceHistory[pixel] = smoothed;
      // Replacement backgrounds use a deliberately tighter matte than blur.
      // This sacrifices a few translucent edge pixels instead of leaking the
      // actual room around hair, shoulders, and fast-moving hands.
      const thresholdStart = effect.value === 'blur' ? 0.48 : 0.58;
      const thresholdRange = effect.value === 'blur' ? 0.24 : 0.22;
      const normalized = Math.max(0, Math.min(1, (smoothed - thresholdStart) / thresholdRange));
      const feathered = normalized * normalized * (3 - (2 * normalized));
      pixels[index] = 255;
      pixels[index + 1] = 255;
      pixels[index + 2] = 255;
      pixels[index + 3] = Math.round(feathered * 255);
    }
    maskAnalysisContext.putImageData(frame, 0, 0);

    maskContext.save();
    maskContext.clearRect(0, 0, width, height);
    maskContext.imageSmoothingEnabled = true;
    maskContext.imageSmoothingQuality = 'high';
    maskContext.drawImage(maskAnalysisCanvas, 0, 0, width, height);
    maskContext.restore();
    return maskCanvas;
  };

  const drawCover = (targetContext, image, width, height, overscan = 0) => {
    const sourceWidth = Number(image?.videoWidth || image?.naturalWidth || image?.width || width);
    const sourceHeight = Number(image?.videoHeight || image?.naturalHeight || image?.height || height);
    const targetWidth = width + (overscan * 2);
    const targetHeight = height + (overscan * 2);
    const scale = Math.max(targetWidth / sourceWidth, targetHeight / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    targetContext.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
  };

  const drawResults = (results) => {
    if (!context || !canvas || !foregroundContext || !foregroundCanvas || effect.value === 'none') return;
    const { width, height } = canvas;
    const personMask = updateSmoothedMask(results.segmentationMask);
    if (!personMask) return;

    // Build the isolated person on a separate transparent surface. The output
    // canvas is always painted with a complete effect layer first, so no raw
    // camera pixels can remain in replacement-background gaps.
    foregroundContext.save();
    foregroundContext.clearRect(0, 0, width, height);
    foregroundContext.globalCompositeOperation = 'source-over';
    foregroundContext.drawImage(personMask, 0, 0, width, height);
    foregroundContext.globalCompositeOperation = 'source-in';
    foregroundContext.drawImage(results.image, 0, 0, width, height);
    foregroundContext.restore();

    context.save();
    context.clearRect(0, 0, width, height);
    context.globalCompositeOperation = 'source-over';
    const selectedBackground = backgroundImages.get(effect.value);
    if (effect.value === 'blur') {
      context.filter = 'blur(32px) saturate(.92)';
      drawCover(context, results.image, width, height, 52);
    } else if (selectedBackground && !(selectedBackground instanceof Promise)) {
      context.filter = 'none';
      drawCover(context, selectedBackground, width, height);
    } else {
      context.fillStyle = '#111827';
      context.fillRect(0, 0, width, height);
    }
    context.filter = 'none';
    context.drawImage(foregroundCanvas, 0, 0, width, height);
    context.restore();
  };

  const initialize = async (stream) => {
    if (segmenter && processedStream.value) return;
    loading.value = true;
    error.value = '';
    sourceStream = stream;
    try {
      const SelfieSegmentation = await loadSegmentationRuntime();
      if (!SelfieSegmentation) throw new Error('Person segmentation is unavailable in this browser.');
      sourceVideo = document.createElement('video');
      sourceVideo.muted = true;
      sourceVideo.playsInline = true;
      sourceVideo.srcObject = sourceStream;
      await sourceVideo.play();
      const settings = sourceStream.getVideoTracks()[0]?.getSettings?.() || {};
      canvas = document.createElement('canvas');
      canvas.width = Math.min(1280, Number(settings.width || 1280));
      canvas.height = Math.min(720, Number(settings.height || 720));
      context = canvas.getContext('2d', { alpha: false, desynchronized: true });
      foregroundCanvas = document.createElement('canvas');
      foregroundCanvas.width = canvas.width;
      foregroundCanvas.height = canvas.height;
      foregroundContext = foregroundCanvas.getContext('2d', { alpha: true, desynchronized: true });
      maskCanvas = document.createElement('canvas');
      maskCanvas.width = canvas.width;
      maskCanvas.height = canvas.height;
      maskContext = maskCanvas.getContext('2d', { alpha: true, desynchronized: true });
      maskAnalysisCanvas = document.createElement('canvas');
      maskAnalysisCanvas.width = Math.min(512, canvas.width);
      maskAnalysisCanvas.height = Math.max(1, Math.round(maskAnalysisCanvas.width * (canvas.height / canvas.width)));
      maskAnalysisContext = maskAnalysisCanvas.getContext('2d', { alpha: true, desynchronized: true, willReadFrequently: true });
      segmenter = new SelfieSegmentation({ locateFile: (file) => `${RUNTIME_PATH}/${file}` });
      // Preserve the camera's existing orientation. The preview should not
      // flip when a background effect is enabled.
      // The general model is more detailed than the lightweight landscape
      // model and is noticeably better at hands, hair, and quick movement.
      segmenter.setOptions({ modelSelection: 0, selfieMode: false });
      segmenter.onResults(drawResults);
      const output = canvas.captureStream(24);
      sourceStream.getAudioTracks().forEach((track) => output.addTrack(track));
      processedStream.value = output;
      active = true;
      scheduleFrame();
    } catch (initializationError) {
      error.value = initializationError?.message || 'The background effect could not be started.';
      throw initializationError;
    } finally {
      loading.value = false;
    }
  };

  const setEffect = async (nextEffect, stream) => {
    const normalized = ['none', 'blur', ...Object.keys(BACKGROUND_PATHS)].includes(nextEffect) ? nextEffect : 'none';
    const previousEffect = effect.value;
    error.value = '';
    loading.value = true;
    try {
      if (normalized === 'none') {
        sourceStream = stream || sourceStream;
        effect.value = 'none';
        active = false;
        if (frameHandle != null) {
          if (sourceVideo && typeof sourceVideo.cancelVideoFrameCallback === 'function') sourceVideo.cancelVideoFrameCallback(frameHandle);
          else window.clearTimeout(frameHandle);
          frameHandle = null;
        }
        return null;
      }
      if (BACKGROUND_PATHS[normalized]) await ensureBackgroundImage(normalized);
      await initialize(stream);
      effect.value = normalized;
      if (!active) {
        active = true;
        scheduleFrame();
      }
      return processedStream.value;
    } catch (effectError) {
      effect.value = previousEffect;
      error.value = effectError?.message || 'The background effect could not be started.';
      throw effectError;
    } finally {
      loading.value = false;
    }
  };

  const currentStream = () => effect.value === 'none' ? sourceStream : processedStream.value;
  const currentVideoTrack = () => currentStream()?.getVideoTracks?.()[0] || sourceStream?.getVideoTracks?.()[0] || null;

  const close = async () => {
    active = false;
    if (frameHandle != null) {
      if (sourceVideo && typeof sourceVideo.cancelVideoFrameCallback === 'function') sourceVideo.cancelVideoFrameCallback(frameHandle);
      else window.clearTimeout(frameHandle);
    }
    processedStream.value?.getVideoTracks().forEach((track) => track.stop());
    await segmenter?.close?.();
    if (sourceVideo) { sourceVideo.pause(); sourceVideo.srcObject = null; }
    processedStream.value = null;
    segmenter = null;
    sourceVideo = null;
    canvas = null;
    context = null;
    foregroundCanvas = null;
    foregroundContext = null;
    maskCanvas = null;
    maskContext = null;
    maskAnalysisCanvas = null;
    maskAnalysisContext = null;
    maskConfidenceHistory = null;
    backgroundImages.clear();
  };

  return { effect, loading, error, processedStream, setEffect, currentStream, currentVideoTrack, close };
}
